import { useId } from 'react';
import {
  STOREFRONT_NEWSLETTER_CONSENT_VERSION,
  STOREFRONT_NEWSLETTER_CONTACT_TYPES,
  type WebsiteNewsletterCollection,
} from '@dukanti/shared';
import { Label } from '@/components/ui/label';
import type { RegionEditor } from '../hooks/use-region-editor';
import { useWebsiteT } from '../hooks/use-website-t';
import { authoredNewsletterCollection } from '../lib/slot-values';

export interface RegionNewsletterFormProps {
  readonly editor: RegionEditor;
}

/** Stages collection settings in the existing atomic region save; never submits a contact. */
export function RegionNewsletterForm({
  editor,
}: RegionNewsletterFormProps): React.JSX.Element | null {
  const { t } = useWebsiteT();
  const id = useId();
  const slot = editor.slots.find((field) => field.type === 'newsletter-collection');
  const state = slot ? editor.values[slot.id] : undefined;
  if (!slot || !state) return null;

  const collection: WebsiteNewsletterCollection = authoredNewsletterCollection(state) ?? {
    enabled: false,
    contactMethods: ['email'],
    consentVersion: STOREFRONT_NEWSLETTER_CONSENT_VERSION,
  };
  const issueKey =
    collection.contactMethods.length === 0
      ? 'admin.websiteTemplates.newsletter.invalid'
      : editor.fieldIssues[slot.id];
  const messageId = `${id}-message`;
  const setCollection = (next: WebsiteNewsletterCollection): void =>
    editor.setField(slot.id, { mode: 'authored', value: { v: next } });

  return (
    <div className="space-y-5" data-testid={`field-${slot.id}`}>
      <Label htmlFor={`${id}-enabled`} className="flex min-h-11 items-center gap-3 text-start">
        <input
          id={`${id}-enabled`}
          type="checkbox"
          checked={collection.enabled}
          className="border-input accent-primary focus-visible:ring-ring size-4 shrink-0 rounded focus-visible:outline-none focus-visible:ring-2"
          onChange={(event) => setCollection({ ...collection, enabled: event.target.checked })}
        />
        <span>{t('admin.websiteTemplates.newsletter.enabled')}</span>
      </Label>
      <fieldset
        className="space-y-3"
        aria-invalid={issueKey === undefined ? undefined : true}
        aria-describedby={issueKey === undefined ? undefined : messageId}
      >
        <legend className="text-foreground mb-3 text-start text-sm font-medium">
          {t('admin.websiteTemplates.newsletter.contactMethods')}
        </legend>
        {STOREFRONT_NEWSLETTER_CONTACT_TYPES.map((method) => (
          <Label
            key={method}
            htmlFor={`${id}-${method}`}
            className="flex min-h-11 items-center gap-3 text-start"
          >
            <input
              id={`${id}-${method}`}
              type="checkbox"
              checked={collection.contactMethods.includes(method)}
              className="border-input accent-primary focus-visible:ring-ring size-4 shrink-0 rounded focus-visible:outline-none focus-visible:ring-2"
              onChange={(event) =>
                setCollection({
                  ...collection,
                  contactMethods: STOREFRONT_NEWSLETTER_CONTACT_TYPES.filter((candidate) =>
                    candidate === method
                      ? event.target.checked
                      : collection.contactMethods.includes(candidate),
                  ),
                })
              }
            />
            <span>{t(`admin.websiteTemplates.newsletter.${method}`)}</span>
          </Label>
        ))}
      </fieldset>
      <p className="text-muted-foreground text-start text-sm">
        {t('admin.websiteTemplates.newsletter.consentVersion')}
      </p>
      {issueKey === undefined ? null : (
        <p id={messageId} role="alert" className="text-destructive text-start text-sm">
          {t(issueKey)}
        </p>
      )}
    </div>
  );
}
